import React, { useState } from "react";
import {
  Avatar,
  Box,
  TextField,
  Button,
  Grid,
  Typography,
  InputAdornment,
  IconButton,
  Tooltip,
  Snackbar,
  FormControlLabel,
  Switch,
  Divider,
  Paper,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import PersonIcon from "@mui/icons-material/Person";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import CloseIcon from "@mui/icons-material/Close";
import Loader from "../loader/loader";
import { useAuth } from "../../services/auth/AuthContext";
import styles from "./styles.module.scss";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";

import {
  WEB_PUSH_KEY_DEFAULT_VALUE,
  WEB_PUSH_KEY_NAME,
} from "../../utils/const";
import { api } from "../../services/api";
import type { PurgeUserRequest } from "../../types/purge";

interface UserProfileProps {
  email?: string;
  family_name?: string;
  given_name?: string;
  phone_number?: string;
  phone_number_verified?: boolean;
  webPush?: string;
  onSave?: (profile: UserProfile) => void;
  handleOnClose?: () => void;
  editProfile?: string;
}

interface UserProfile {
  email: string;
  family_name: string;
  given_name: string;
  phone_number: string;
  "custom:webPush": string;
  "custom:editProfile": string | undefined;
}

const UserProfile: React.FC<UserProfileProps> = ({
  email = "",
  family_name = "",
  given_name = "",
  phone_number = "",
  phone_number_verified,
  onSave,
  handleOnClose,
  webPush,
  editProfile,
}) => {
  const {
    updateUser,
    confirmUserNumber,
    validatePhoneNumber,
    userAttributes,
    handleLoadUserAttributes,
  } = useAuth();

  const [profile, setProfile] = useState<UserProfile>({
    email: userAttributes ? userAttributes.email! : email,
    family_name: userAttributes ? userAttributes.family_name! : family_name,
    given_name: userAttributes ? userAttributes.given_name! : given_name,
    phone_number: userAttributes
      ? userAttributes?.phone_number! === "+50611111111"
        ? ""
        : userAttributes?.phone_number!
      : phone_number,
    "custom:webPush": userAttributes
      ? userAttributes["custom:webPush"]!
      : webPush || WEB_PUSH_KEY_DEFAULT_VALUE,
    "custom:editProfile": editProfile,
  });

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [sendingPhoneCode, setSendingPhoneCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackBarOpen, setSAnackBarOpen] = useState(false);
  const [snackBarMessage, setSnackBarMessage] = useState("");
  const [perfilMode, setPerfilMode] = useState(0);
  const [verificationCode, setVerificationCode] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [purging, setPurging] = useState(false);

  const { userAttributes: attrs, signOut } = useAuth() as any;

  const cleanupClientSide = async () => {
    try {
      if ("serviceWorker" in navigator) {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (sub) await sub.unsubscribe();
      }
    } catch {}
    localStorage.clear();
    sessionStorage.clear();
  };

  // handler confirmar baja
  const handleConfirmPurge = async () => {
    try {
      setPurging(true);
      const sub = attrs?.sub;
      if (!sub) throw new Error("No se encontró el sub del usuario actual.");
      const body: PurgeUserRequest = {
        body: {
          sub: sub,
        },
      };
      // Llamada al endpoint
      await api.users.purge(body);

      setSnackBarMessage("Usuario dado de baja correctamente");
      setSAnackBarOpen(true);

      // Espera 2 segundos para que el usuario vea el mensaje
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Limpieza de storage, subscripciones, etc.
      await cleanupClientSide();

      // Redirección o signOut
      if (typeof signOut === "function") {
        await signOut();
      } else {
        window.location.replace("/");
      }
    } catch (e: any) {
      setSnackBarMessage(
        `Error al dar de baja: ${e?.message || "Inténtalo más tarde"}`
      );
      setSAnackBarOpen(true);
    } finally {
      setPurging(false);
      setConfirmOpen(false);
    }
  };

  const snackBar = () => (
    <Snackbar
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      open={snackBarOpen}
      onClose={() => setSAnackBarOpen(false)}
      message={snackBarMessage}
      autoHideDuration={4000}
      key="snackbar-profile-message"
      ContentProps={{
        sx: {
          backgroundColor: snackBarMessage.includes("correctamente")
            ? "error.main"
            : "success.main",
        },
      }}
    />
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfile((prevProfile) => ({ ...prevProfile, [name]: value }));
  };

  const handleEditToggle = () => setIsEditing((prev) => !prev);

  const subscribeUserToPush = async (
    registration: ServiceWorkerRegistration
  ) => {
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey:
        "BBSeCmOg8peLyH7eMRB9xR0NrSFVzUnQduY7v-xlwHCRVYJ65GNAGLpQeFOdxwqoKkmJcZI8u_d5NWzbwWZsNHA",
    });
    return subscription;
  };

  const askPermission = () => {
    Notification.requestPermission().then((response) => {
      if (response === "granted") {
        navigator.serviceWorker.ready.then(async (registration) => {
          const resp = await subscribeUserToPush(registration);
          setProfile((prevProfile) => ({
            ...prevProfile,
            [WEB_PUSH_KEY_NAME]: JSON.stringify(resp),
          }));
        });
      }
    });
  };

  const handleSave = async () => {
    onSave?.(profile);
    const profileAttri = { ...profile };
    setLoading(true);

    // Normaliza teléfono con prefijo +506 (vacío -> placeholder)
    profileAttri.phone_number =
      profileAttri.phone_number === ""
        ? "+50611111111"
        : `+506${profileAttri.phone_number.replace("+506", "")}`;

    profileAttri["custom:editProfile"] = "true";

    const response = await updateUser(profileAttri);
    handleLoadUserAttributes();
    setSnackBarMessage(
      response
        ? "Perfil actualizado exitosamente"
        : "Error al actualizar los datos, inténtalo más tarde"
    );
    setSAnackBarOpen(true);
    setIsEditing(false);
    setLoading(false);
  };

  const validateUserPhoneNumber = async () => {
    const response = await validatePhoneNumber(verificationCode);
    if (response) {
      setSnackBarMessage("Número verificado correctamente");
      setSAnackBarOpen(true);
      window.location.reload();
    } else {
      setSnackBarMessage("Código de verificación incorrecto");
      setSAnackBarOpen(true);
    }
  };

  const initials =
    (profile.given_name?.[0] || "") + (profile.family_name?.[0] || "");

  return (
    <Box
      sx={{
        p: 0,
        m: "0 auto",
        position: "relative",
        width: { xs: "100%", sm: 460 },
      }}
    >
      {/* Cerrar */}
      <IconButton
        onClick={handleOnClose}
        className={`${styles.custom_close_button}`}
        aria-label="close"
        sx={{ position: "absolute", top: 8, right: 8, zIndex: 2 }}
      >
        <CloseIcon />
      </IconButton>

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          p: 3,
          pb: 2,
        }}
      >
        <Avatar sx={{ bgcolor: "primary.main", width: 48, height: 48 }}>
          {initials.toUpperCase() || "U"}
        </Avatar>
        <Box>
          <Typography variant="h6" fontWeight={800}>
            Perfil de Usuario
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Gestiona tu información personal y tus preferencias de notificación
          </Typography>
        </Box>
      </Box>

      <Divider />

      {/* Contenido */}
      {perfilMode === 0 && (
        <Box sx={{ p: 3 }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <TextField
                label="Email"
                name="email"
                value={profile.email}
                onChange={handleChange}
                fullWidth
                disabled
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Nombre"
                name="given_name"
                value={profile.given_name}
                onChange={handleChange}
                fullWidth
                disabled={!isEditing}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Apellido"
                name="family_name"
                value={profile.family_name}
                onChange={handleChange}
                fullWidth
                disabled={!isEditing}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Número de teléfono (+506)"
                name="phone_number"
                value={profile.phone_number.replace("+506", "")}
                onChange={handleChange}
                disabled={!isEditing}
                type="tel"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocalPhoneIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment:
                    !phone_number_verified && profile.phone_number ? (
                      <InputAdornment position="end">
                        <Tooltip title="Enviar código de verificación">
                          <span>
                            <IconButton
                              disabled={
                                profile.phone_number === "" || sendingPhoneCode
                              }
                              onClick={() => {
                                setSendingPhoneCode(true);
                                confirmUserNumber().then(() => {
                                  setSendingPhoneCode(false);
                                  setPerfilMode(1);
                                });
                              }}
                              color="primary"
                              size="small"
                            >
                              <NotificationsActiveIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </InputAdornment>
                    ) : undefined,
                }}
                helperText={
                  !phone_number_verified && profile.phone_number
                    ? "Tu número no está verificado."
                    : " "
                }
              />
            </Grid>

            {/* Notificaciones web */}
            <Grid item xs={12}>
              <Box sx={{ mb: 0.5 }}>
                <Typography variant="subtitle2">Notificaciones web</Typography>
                <Typography variant="body2" color="text.secondary">
                  Recibe alertas directamente en tu navegador cuando el camión
                  esté cerca
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Switch
                    onChange={(event) => {
                      if (event.target.checked) {
                        if ("serviceWorker" in navigator) {
                          askPermission();
                        }
                      } else {
                        setProfile((prevProfile) => ({
                          ...prevProfile,
                          [WEB_PUSH_KEY_NAME]: WEB_PUSH_KEY_DEFAULT_VALUE,
                        }));
                      }
                    }}
                    disabled={!isEditing}
                    checked={
                      profile[WEB_PUSH_KEY_NAME] !==
                        WEB_PUSH_KEY_DEFAULT_VALUE &&
                      !!profile[WEB_PUSH_KEY_NAME]
                    }
                  />
                }
                label=""
              />
            </Grid>

            {/* Botón principal */}
            <Grid item xs={12}>
              {isEditing ? (
                <Box sx={{ position: "relative" }}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={loading}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      py: 1.2,
                      borderRadius: 2,
                    }}
                  >
                    Guardar
                  </Button>
                  {loading && (
                    <Box sx={{ mt: 1, display: "grid", placeItems: "center" }}>
                      <Loader />
                    </Box>
                  )}
                </Box>
              ) : (
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={handleEditToggle}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    py: 1.2,
                    borderRadius: 2,
                  }}
                >
                  Editar
                </Button>
              )}
              <Button
                fullWidth
                variant="outlined"
                color="error"
                startIcon={<DeleteForeverIcon />}
                onClick={() => setConfirmOpen(true)}
                sx={{
                  mt: 1.5,
                  textTransform: "none",
                  fontWeight: 700,
                  py: 1.1,
                  borderRadius: 2,
                }}
              >
                Darse de baja
              </Button>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Modo verificación */}
      {perfilMode === 1 && (
        <Box sx={{ p: 3 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            Verificar número de teléfono
          </Typography>
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <TextField
                disabled
                fullWidth
                label="Número de Teléfono (+506)"
                value={profile.phone_number.replace("+506", "")}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocalPhoneIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Código de verificación"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                disabled={verificationCode === ""}
                onClick={validateUserPhoneNumber}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  py: 1.2,
                  borderRadius: 2,
                }}
              >
                Validar código de verificación
              </Button>
              <Button
                fullWidth
                onClick={() => setPerfilMode(0)}
                sx={{ mt: 1, textTransform: "none" }}
              >
                Regresar
              </Button>
            </Grid>
          </Grid>
        </Box>
      )}

      {snackBar()}
      <Dialog
        open={confirmOpen}
        onClose={() => !purging && setConfirmOpen(false)}
      >
        <DialogTitle>Confirmar baja de cuenta</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 1 }}>
            ¿Estás seguro de que deseas darte de baja? <br />
            <strong>
              Todos tus datos y suscripciones serán eliminados de forma
              permanente.
            </strong>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmOpen(false)}
            disabled={purging}
            sx={{ textTransform: "none" }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmPurge}
            color="error"
            variant="contained"
            disabled={purging}
            sx={{ textTransform: "none" }}
          >
            {purging ? "Eliminando…" : "Sí, dar de baja"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserProfile;
